import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import ReCAPTCHA from 'react-google-recaptcha';
import { sendContactMessage } from '../api/contact';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    reason: '',
    message: '',
    agreed: false
  });

  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [selectTouched, setSelectTouched] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!captchaValue) return;

    setIsLoading(true);
    setShowError(false);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phone,
        companyName: formData.company,
        reasonOfContacting: formData.reason,
        whatLookingFor: formData.message,
      };

      await sendContactMessage(payload);

      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        reason: '',
        message: '',
        agreed: false,
      });
      setSelectTouched(false);
      setCaptchaValue(null);
      
      // Hide success message after 5 seconds
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error: any) {
      setShowError(true);
      // Hide error message after 5 seconds
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
  };

  return (
    <div className="min-h-screen relative">
      {/* Success Alert */}
      {showSuccess && (
        <div className="fixed inset-x-0 top-[50px] mx-auto w-max z-50">
          <div className="border border-gray-200 shadow-lg rounded-lg px-6 py-4 items-center w-[350px] h-[120px] bg-[#CEEAD6]">
            <div className='flex'>
              <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12.185" cy="12.185" r="12.185" fill="#17752C"/>
                <path d="M17.861 8.27349C18.1792 8.56721 18.1792 9.08122 17.861 9.37494L11.595 15.641C11.3012 15.9592 10.7872 15.9592 10.4935 15.641L7.36048 12.508C7.04228 12.2142 7.04228 11.7002 7.36048 11.4065C7.6542 11.0883 8.16821 11.0883 8.46193 11.4065L11.0565 13.9766L16.7595 8.27349C17.0533 7.95529 17.5673 7.95529 17.861 8.27349Z" fill="white"/>
              </svg>
              <p className="font-semibold text-gray-900 ml-4 pb-5">Success</p>
            </div>
            <div>
              <p className="font-medium text-[13.88px] leading-[19.27px] tracking-[-2%] text-black/40">Thanks for getting in touch! We’ll reach out to help you as soon as possible.</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Alert */}
      {showError && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error! </strong>
            <span className="block sm:inline">Failed to send message. Please try again.</span>
            <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setShowError(false)}>
              <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <title>Close</title>
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
              </svg>
            </span>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-[400px] mt-16 pt-16">
        <img
          src="/contact-background.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div>
            <p className="font-normal text-[14px] leading-[19.03px] tracking-[0] text-white ml-[5px]">GET IN TOUCH</p>
            <h1 className="font-light text-[40px] leading-[50px] sm:text-[60px] sm:leading-[80px] md:text-[80px] md:leading-[100px] lg:leading-[130.54px] tracking-[0] text-white">
              How can we help?
            </h1>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <h2 className="font-normal text-[22px] leading-[153%] tracking-[0%] font-inter mb-2">Get in touch with us</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Input
                  placeholder="Your name*"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="h-12 border border-black text-gray-600 placeholder:text-gray-400"
                  required
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Email address*"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="h-12 border border-black text-gray-600 placeholder:text-gray-400"
                  required
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Input
                  placeholder="Phone number*"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="h-12 border border-black text-gray-600 placeholder:text-gray-400"
                  required
                />
              </div>
              <div>
                <Input
                  placeholder="Company name*"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="h-12 border border-black text-gray-600 placeholder:text-gray-400"
                  required
                />
              </div>
            </div>

            <div>
              <Select 
                onValueChange={(value) => {
                  setFormData({...formData, reason: value});
                  setSelectTouched(true);
                }}
                required
                value={formData.reason}
              >
                <SelectTrigger className={`h-12 border-black text-gray-400 ${
                  selectTouched && !formData.reason ? 'border-red-500' : ''
                }`}>
                  <SelectValue placeholder="Reason for contacting NV Global*" />
                </SelectTrigger>
                <SelectContent className="bg-white" position="popper" style={{ pointerEvents: 'auto' }}>
                  <SelectItem value="I'd like to work with NV Global">I'd like to work with NV Global</SelectItem>
                  <SelectItem value="I'd like to partner with NV Global">I'd like to partner with NV Global</SelectItem>
                  <SelectItem value="Press inquiry">Press inquiry</SelectItem>
                  <SelectItem value="Speaking engagement request">Speaking engagement request</SelectItem>
                  <SelectItem value="I'd like to explore career opportunities at NV Global">I'd like to explore career opportunities at NV Global</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {selectTouched && !formData.reason && (
                <p className="mt-1 text-sm text-red-500">Please select a reason</p>
              )}
            </div>

            <div>
              <Textarea
                placeholder="Please tell us a bit about what you're looking for*"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="min-h-[120px] border-black text-gray-600 placeholder:text-gray-400 resize-none"
                required
              />
            </div>

            <div className="flex items-center space-x-2">
              <ReCAPTCHA
                sitekey="6LfZQmUrAAAAAF3Qb8jKPUXx4MGsZV346uOcp84Z"
                onChange={handleCaptchaChange}
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                className="bg-black flex text-white px-8 py-3 hover:bg-gray-800 transition-colors font-medium tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!captchaValue || isLoading || !formData.reason}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    SENDING...
                  </>
                ) : (
                  <>
                    SUBMIT <svg className='ml-[10px] self-center' width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8.75 1C8.75 0.585787 8.41421 0.25 8 0.25L1.25 0.25C0.835786 0.25 0.5 0.585786 0.5 1C0.5 1.41421 0.835786 1.75 1.25 1.75H7.25V7.75C7.25 8.16421 7.58579 8.5 8 8.5C8.41421 8.5 8.75 8.16421 8.75 7.75L8.75 1ZM1 8L1.53033 8.53033L8.53033 1.53033L8 1L7.46967 0.46967L0.46967 7.46967L1 8Z" fill="white"/>
                    </svg>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;